import { CHARACTERS, NPC_DEFINITIONS } from '../data/characters';
import type { Wallet } from '../systems/WalletSystem';

export type NpcCategory = 'ambient' | 'interactive' | 'reward' | 'penalty' | 'recurring';

export interface NpcData {
  type: string;
  category: NpcCategory;
  x?: number;
  y?: number;
  width: number;
  height: number;
  interactRadius: number;
  reward?: { type: 'CUP' | 'USD' | 'EUR'; amount: number };
  penalty?: { type: 'CUP' | 'USD' | 'EUR'; amount: number };
  /**
   * When false (default) the NPC bounces at the edges of the playable world
   * and never leaves the game. Set true to let this NPC wander/persuade off
   * the level (not recommended for the initial milestone).
   */
  canLeaveWorld?: boolean;
}

export class Npc implements InteractableEntity {
  public type: string;
  public category: NpcCategory;
  public x: number = 0;
  public y: number = 0;
  public width: number;
  public height: number;
  public interactRadius: number;
  public reward?: { type: 'CUP' | 'USD' | 'EUR'; amount: number };
  public penalty?: { type: 'CUP' | 'USD' | 'EUR'; amount: number };
  public collected: boolean = false;
  /** Killed by a thrown stone — stays on screen lying on the ground. */
  public killed: boolean = false;
  // Ambient patrol: NPCs walk back and forth within [patrolMinX, patrolMaxX].
  public patrolMinX: number = 0;
  public patrolMaxX: number = 1;
  public patrolSpeed: number = 0;
  public patrolDir: 1 | -1 = 1;
  public canLeaveWorld: boolean = false;

  constructor(data: NpcData) {
    this.type = data.type;
    this.category = data.category;
    this.x = data.x ?? 0;
    this.y = data.y ?? 0;
    this.canLeaveWorld = data.canLeaveWorld ?? false;
    const charData = CHARACTERS[data.type] || CHARACTERS.civilian;
    const npcDef = NPC_DEFINITIONS[data.type] || NPC_DEFINITIONS.civilian;
    this.width = data.width || charData.width;
    this.height = data.height || charData.height;
    this.interactRadius = data.interactRadius || npcDef.interactRadius;
    this.reward = data.reward;
    this.penalty = data.penalty;
  }

  /**
   * Advance ambient patrol movement within the given world bounds (in px).
   * Returns the new x. The walk bounces (reverses) when reaching either edge.
   */
  updatePatrol(deltaTime: number, minX: number = this.patrolMinX, maxX: number = this.patrolMaxX): number {
    this.x += this.patrolDir * this.patrolSpeed * (deltaTime / 16.67);
    if (this.x <= minX) {
      this.x = minX;
      this.patrolDir = 1;
    } else if (this.x >= maxX) {
      this.x = maxX;
      this.patrolDir = -1;
    }
    return this.x;
  }

  getHitbox(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.x - this.width / 2,
      y: this.y - this.height,
      width: this.width,
      height: this.height,
    };
  }

  interact(wallet: Wallet): void {
    if (this.reward) {
      wallet.add(this.reward.type.toUpperCase() as 'CUP' | 'USD' | 'EUR', this.reward.amount);
    }
    if (this.penalty) {
      wallet.add(this.penalty.type.toUpperCase() as 'CUP' | 'USD' | 'EUR', -this.penalty.amount);
    }
  }

  collect(wallet: Wallet): void {
    this.interact(wallet);
  }

  markCollected(): void {
    this.collected = true;
  }

  /** Killed by a stone: counts as collected (no more interaction) + dead pose. */
  markKilled(): void {
    this.killed = true;
    this.collected = true;
  }

  hasBeenCollected(): boolean {
    return this.collected;
  }

  get isPowerup(): boolean {
    return false;
  }

  get id(): string {
    return `npc-${this.type}`;
  }
}

export interface InteractableEntity {
  getHitbox(): { x: number; y: number; width: number; height: number };
  collect(wallet: Wallet): void;
  markCollected(): void;
  hasBeenCollected(): boolean;
  isPowerup: boolean;
  id: string;
}
